import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import apiConfig from "../utils/apiConfig";
import useNotification from "../components/notifications/useNotifications";
import { BulkRelistResultData } from "../components/ebayListings/BulkRelistResultModal";
import { DefaultResponseWithData } from "../models/system";

// Mirrors GPMetals.Core.Helpers.DefaultResponse<BulkRelistResultResponse>
export interface IBulkRelistResponse {
  success: boolean;
  message: string;
  data: BulkRelistResultData;
}

export default function useBatchListingsMutations(componentName = "useBatchListingsMutations") {
  const { get, post, put } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60; //1 minutes

  const { addNotification } = useNotification();

  const createBatch = useMutation(
    (body: any) => post(`/part/listings/multiple/batch`, body),
    {
      onSuccess: (data, _, __) => {
        // queryClient.invalidateQueries(["parts", "multiple"]);
        return data;
      },
    }
  );

  // Data is the correlation id the batch was queued under - poll it via usePublishResults.
  const bulkPublish = useMutation(
    (body: any) => post<any, DefaultResponseWithData<string>>(`/bulklisting/publish`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const bulkRelist = useMutation(
    (body: any) => post<any, IBulkRelistResponse>(`/bulklisting/relist`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["pagedTable"]);
      },
    }
  );

  const cancelMutation = useMutation(
    (batchItemId: string) => put(`/bulklisting/batch-item/${batchItemId}/cancel`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
        queryClient.invalidateQueries({ queryKey: ["bulkListing", "stats"] });
        addNotification({ variant: "success", primaryText: "Listing cancelled" });
      },
      onError: () => {
        addNotification({ variant: "error", primaryText: "Failed to cancel listing" });
      },
    }
  );

  return {
    createBatch,
    bulkPublish,
    bulkRelist,
    cancelMutation,
  };
}
